import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import { BsTelephoneOutbound, BsCalendarEvent } from "react-icons/bs";
import { toast } from "react-toastify";

interface Props {
  show: boolean;
  onHide: () => void;
  defaultType?: string;
  contactName?: string;
  contactEmail?: string;
  userType?: string;
}

const RmLogActivityModal = ({
  show,
  onHide,
  contactName = "",
  contactEmail = "",
  userType = "ENTREPRENEUR",
}: Props) => {
  const [name, setName] = useState(contactName);
  const [email, setEmail] = useState(contactEmail);

  // Call state
  const [callDuration, setCallDuration] = useState("10 mins");
  const [callOutcome, setCallOutcome] = useState("POSITIVE");
  const [callNotes, setCallNotes] = useState("");
  const [followupDate, setFollowupDate] = useState("");

  useEffect(() => {
    setName(contactName);
    setEmail(contactEmail);
  }, [contactName, contactEmail, show]);

  const handleSaveLog = () => {
    if (!name.trim()) {
      toast.error("Please enter contact person name.");
      return;
    }

    if (!callNotes.trim()) {
      toast.error("Please enter call notes/summary.");
      return;
    }

    const newCallLog = {
      id: "call_" + Date.now(),
      contact_name: name,
      contact_email: email,
      user_type: userType,
      duration: callDuration,
      outcome: callOutcome,
      notes: callNotes,
      followup_date: followupDate,
      logged_at: new Date().toISOString(),
    };

    const existing = JSON.parse(localStorage.getItem("rm_call_logs") || "[]");
    localStorage.setItem("rm_call_logs", JSON.stringify([newCallLog, ...existing]));
    toast.success("Call activity logged successfully!");

    window.dispatchEvent(new Event("rm_logs_updated"));
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
      <Modal.Header closeButton className="border-bottom pb-3">
        <Modal.Title className="font-18 fw-bold">
          <span className="text-primary d-flex align-items-center gap-2">
            <BsTelephoneOutbound /> Log Call Activity
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-3">
        <Row className="g-3 mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold font-13">Contact Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-semibold font-13">Email / Contact info</Form.Label>
              <Form.Control
                type="text"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-semibold font-13">Call Duration</Form.Label>
              <Form.Select value={callDuration} onChange={(e) => setCallDuration(e.target.value)}>
                <option value="5 mins">5 mins</option>
                <option value="10 mins">10 mins</option>
                <option value="15 mins">15 mins</option>
                <option value="30 mins">30 mins</option>
                <option value="45+ mins">45+ mins</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-semibold font-13">Call Outcome</Form.Label>
              <Form.Select value={callOutcome} onChange={(e) => setCallOutcome(e.target.value)}>
                <option value="POSITIVE">Positive / Interested</option>
                <option value="FOLLOWUP_NEEDED">Followup Needed</option>
                <option value="NO_ANSWER">No Answer / Busy</option>
                <option value="NOT_INTERESTED">Not Interested</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-semibold font-13">Next Follow-up Date</Form.Label>
              <InputGroup>
                <InputGroup.Text><BsCalendarEvent /></InputGroup.Text>
                <Form.Control
                  type="date"
                  value={followupDate}
                  onChange={(e) => setFollowupDate(e.target.value)}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold font-13">Call Discussion Summary & Notes</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            placeholder="Enter call notes, key discussion points, and agreed next steps..."
            value={callNotes}
            onChange={(e) => setCallNotes(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="border-top pt-2">
        <Button variant="light" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSaveLog}>
          Save Call Log
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RmLogActivityModal;
